import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useImageComponent } from '../utils/component';
import { Cta } from './cta';
import { mq, DEVICE_SIZE } from '../utils/mq';
import { useTranslation } from '../utils/translation';

const Comparative = styled.div`
  overflow-x: auto;

  ${mq.lte(
    DEVICE_SIZE.MEDIUM,
    css`
      width: calc(100vw - 20px);
    `,
  )}
`;

const ComparativeTable = styled.table`
  border-collapse: initial;
  border-spacing: 2px 0;
  font-size: 14px;
  text-align: center;
  width: 100%;
`;

const ComparativeTableWrapper = styled.div`
  overflow-x: auto;
  padding: var(--spacer) 0 var(--spacer-xl);

  ${mq.lte(
    DEVICE_SIZE.MEDIUM,
    css`
      width: calc(100vw - 20px);
    `,
  )}
`;

const Th = styled.th`
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  font-weight: 600;
  line-height: 20px;
  min-width: 160px;
  padding: 15px;
  position: relative;

  p {
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    bottom: 0;
    font-size: 14px;
    font-weight: 600 !important;
    left: 0;
    line-height: 20px !important;
    padding: 15px 0;
    position: absolute;
    width: 100%;
    margin: 0;
  }

  &.mod-featured:nth-of-type(odd) p {
    background: none;
  }

  &:nth-of-type(odd) p {
    background: var(--color-alt-5);
  }

  img {
    display: none;
  }

  span {
    opacity: 0;
    position: absolute;
    top: -15px;
  }

  &.mod-featured {
    background: #28283b;
    border-left: solid 6px var(--theme-color-background);
    border-right: solid 6px var(--theme-color-background);

    picture {
      margin: 0 auto var(--spacer-xs);
    }

    img {
      border-radius: 8px;
      display: initial;
    }

    p {
      color: white;
      position: relative;
      padding: 2px;
    }

    span {
      background: #28283b;
      left: 0;
      opacity: 1;
      right: 0;
    }
  }

  &:first-of-type {
    background: none;
    min-width: initial;
    text-align: left;
  }
`;

const Td = styled.td`
  line-height: 20px;
  min-width: 121px;
  padding: 10px;
  text-align: center;

  &:nth-of-type(odd) {
    background: var(--color-alt-5);
  }

  &:first-of-type {
    background: none;
    min-width: initial;
    text-align: left;
  }

  &.mod-featured {
    background: #28283b;
    border-left: solid 6px var(--theme-color-background);
    border-right: solid 6px var(--theme-color-background);
    color: white;
    font-weight: 600;
    position: relative;
  }

  &.mod-last {
    position: relative;
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }
`;

const ComparativeTableCta = styled.div`
  display: none;
  position: absolute;
  bottom: -57px;
  z-index: 1;
  left: 0;
  right: 0;
  background: #28283b;
  padding: 12px;
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;

  .mod-featured & {
    display: block;
  }
`;

interface Criterion {
  label: string;
  key: string;
}

interface Value {
  title: string;
  cta: string;
  [key: string]: any;
}

interface TableMultipleComparativeProps {
  criteria: Criterion[];
  values: Value[];
}

export const TableMultipleComparative: React.FC<TableMultipleComparativeProps> = ({ criteria, values }) => {
  const [active, setActive] = useState<number | null>(0);
  const ImageComponent = useImageComponent();

  const imageProps = {
    mobileWidth: 40,
    mobileHeight: 40,
    desktopWidth: 40,
    desktopHeight: 40,
  };

  const selectIndex = (index: number) => {
    setActive(index);
  };

  const t = useTranslation();

  return (
    <Comparative>
      <ComparativeTableWrapper>
        <ComparativeTable>
          <thead>
            <tr>
              <Th />{' '}
              {values.map((value, index) => (
                <Th
                  key={index}
                  className={index === active ? 'mod-featured' : ''}
                  onMouseOver={() => selectIndex(index)}
                >
                  <ImageComponent {...imageProps} imageId={value?.imageId} alt={value?.title} lazy={false} />
                  <p>{value.title}</p>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {criteria.map((criterion, index) => (
              <tr key={index}>
                <Td>{criterion.label}</Td>
                {values.map((value, indexValue) => {
                  const classNames = `${indexValue === active ? 'mod-featured' : ''}${
                    index === criteria.length - 1 ? ' mod-last' : ''
                  }`;

                  return (
                    <Td key={indexValue} className={classNames} onMouseOver={() => selectIndex(indexValue)}>
                      {value[criterion.key]}
                      {index === criteria.length - 1 && (
                        <ComparativeTableCta>
                          <Cta className="mod-sm" href={value.cta} rel={['nofollow']} openInNewWindow>
                            {t('design.dataSheet.seeOffer')}
                          </Cta>
                        </ComparativeTableCta>
                      )}
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </ComparativeTable>
      </ComparativeTableWrapper>
    </Comparative>
  );
};

export default TableMultipleComparative;
